import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { RealizedMissing } from '@/features/orders/api/orders';
import { apiRequest } from '@/lib/http';

export type FixedExpenseCategory =
  'PEOPLE' | 'OCCUPANCY' | 'SERVICES' | 'SOFTWARE' | 'MARKETING' | 'FINANCIAL' | 'OTHER';

export const CATEGORY_LABELS: Record<FixedExpenseCategory, string> = {
  PEOPLE: 'Pessoal',
  OCCUPANCY: 'Ocupação (aluguel, energia)',
  SERVICES: 'Serviços (contador, jurídico)',
  SOFTWARE: 'Sistemas',
  MARKETING: 'Marketing fora das plataformas',
  FINANCIAL: 'Financeiras (tarifas bancárias)',
  OTHER: 'Outras',
};

export interface FixedExpense {
  id: string;
  name: string;
  category: FixedExpenseCategory;
  amount: string;
  /** `AAAA-MM`. */
  startMonth: string;
  endMonth: string | null;
  /** Dia do vencimento; nulo = não informado (a previsão de caixa conta no dia 1). */
  dueDay: number | null;
}

export interface FinanceProduct {
  productId: string | null;
  sku: string | null;
  title: string;
  units: number;
  revenue: string;
  result: string;
  margin: string | null;
  abcRevenue: 'A' | 'B' | 'C';
  abcResult: 'A' | 'B' | 'C';
}

/** Resultado do mês pelo dinheiro que entrou (ADR-050). */
export interface MonthlyFinance {
  month: string;
  consolidated: boolean;
  orders: number;
  cash: {
    grossReceived: string;
    platformCosts: string;
    netReceived: string;
    otherCosts: string;
    result: string;
  };
  dre: {
    grossRevenue: string;
    taxes: string;
    netRevenue: string;
    productCost: string;
    saleFees: string;
    shipping: string;
    packaging: string;
    additional: string;
    /** Custo de devolução cobrado pela plataforma. */
    returns: string;
    contributionMargin: string;
    contributionMarginRate: string | null;
    fixedExpenses: string | null;
    operatingResult: string | null;
    breakEven: string | null;
  };
  indicators: {
    marketplaceCostRate: string | null;
    averageTicket: string | null;
    resultPerOrder: string | null;
  };
  completeness: {
    incompleteOrders: number;
    approximateOrders: number;
    missing: Record<RealizedMissing, number>;
  };
  products: FinanceProduct[];
  fixedExpenseLines: FixedExpense[] | null;
  releasePermissionMissing: string[];
  /** Vendas entregues ainda sem data de liberação consultada: sem mês no caixa por enquanto. */
  releaseDatePending: number;
}

export interface Receivable {
  total: string;
  orders: number;
  scheduled: { day: string; amount: string; orders: number }[];
  scheduledTotal: { amount: string; orders: number };
  /** Pagos e ainda não entregues: a data sai depois da entrega. */
  awaitingDelivery: { amount: string; orders: number };
  /** Entregues com a data de liberação ainda por consultar — fora do total. */
  releaseDatePending: number;
}

/** Consolidado = sem plataforma. */
export interface FinanceScope {
  provider?: string;
}

export const financeKeys = {
  all: ['finance'] as const,
  month: (month: string, scope: FinanceScope) =>
    [...financeKeys.all, 'month', month, scope.provider ?? ''] as const,
  receivable: (scope: FinanceScope) =>
    [...financeKeys.all, 'receivable', scope.provider ?? ''] as const,
  expenses: () => [...financeKeys.all, 'fixed-expenses'] as const,
};

const scopeParams = (scope: FinanceScope): Record<string, string> =>
  scope.provider ? { provider: scope.provider } : {};

export function useMonthlyFinance(month: string, scope: FinanceScope) {
  return useQuery({
    queryKey: financeKeys.month(month, scope),
    queryFn: () =>
      apiRequest<MonthlyFinance>(
        `/finance/month?${new URLSearchParams({ month, ...scopeParams(scope) }).toString()}`,
      ),
    placeholderData: keepPreviousData,
  });
}

export function useReceivable(scope: FinanceScope) {
  return useQuery({
    queryKey: financeKeys.receivable(scope),
    queryFn: () =>
      apiRequest<Receivable>(
        `/finance/receivable?${new URLSearchParams(scopeParams(scope)).toString()}`,
      ),
  });
}

export function useFixedExpenses() {
  return useQuery({
    queryKey: financeKeys.expenses(),
    queryFn: () => apiRequest<FixedExpense[]>('/finance/fixed-expenses'),
  });
}

/** Despesa mudou: a lista e todo resultado de mês consolidado recarregam. */
function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: financeKeys.all });
}

export function useCreateFixedExpense() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: Omit<FixedExpense, 'id'>) =>
      apiRequest<FixedExpense>('/finance/fixed-expenses', { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

export function useChangeFixedExpense() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; amount: string; fromMonth: string }) =>
      apiRequest<FixedExpense>(`/finance/fixed-expenses/${id}/amount`, { method: 'PATCH', body }),
    onSuccess: invalidate,
  });
}

export function useEndFixedExpense() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, lastMonth }: { id: string; lastMonth: string }) =>
      apiRequest<FixedExpense>(`/finance/fixed-expenses/${id}/end`, {
        method: 'PATCH',
        body: { lastMonth },
      }),
    onSuccess: invalidate,
  });
}

export function useDeleteFixedExpense() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/finance/fixed-expenses/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/** `AAAA-MM` do mês atual e dos anteriores, para o seletor de mês. */
export function recentMonths(count = 13, now: Date = new Date()): string[] {
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  });
}

export function monthLabel(month: string): string {
  const [year, index] = month.split('-').map(Number);
  const label = new Date(year!, index! - 1, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}
